import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { formatDate } from '@/pages/painel/lib/radarConstants';
import {
  calcularTriagem,
  ObraEnriquecida,
  ObraCompleta,
  TriagemDecisao,
  OportunidadeVinculo,
  NATUREZA_OBRA_LABELS,
  TRIAGEM_FAIXAS,
  MOTIVO_DESCARTE_OBRA_LABELS,
} from '@/pages/painel/lib/triagem';
import { TriagemDetailModal, TriagemDescarteModal } from './TriagemModals';

interface RadarTriagemProps {
  password: string;
}

function faixaInfo(key: string) {
  return TRIAGEM_FAIXAS.find((f) => f.key === key) ?? TRIAGEM_FAIXAS[TRIAGEM_FAIXAS.length - 1];
}

function formatArea(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} m²`;
}

function segmentoTone(segmento: string | null): string {
  const s = String(segmento ?? '').toLowerCase();
  if (s.includes('institucional')) return 'bg-blue-50 text-blue-700';
  if (s.includes('corporativo')) return 'bg-[#E8610A]/10 text-[#E8610A]';
  if (s.includes('residencial')) return 'bg-amber-50 text-amber-700';
  return 'bg-gray-100 text-gray-600';
}

export default function RadarTriagem({ password }: RadarTriagemProps) {
  const [obras, setObras] = useState<ObraCompleta[]>([]);
  const [decisoes, setDecisoes] = useState<Record<string, TriagemDecisao>>({});
  const [oportunidades, setOportunidades] = useState<Record<string, OportunidadeVinculo>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [faixa, setFaixa] = useState('');
  const [segmento, setSegmento] = useState('');
  const [natureza, setNatureza] = useState('');
  const [situacao, setSituacao] = useState('');
  const [cidade, setCidade] = useState('');
  const [uf, setUf] = useState('');
  const [fonte, setFonte] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [search, setSearch] = useState('');

  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [detalhe, setDetalhe] = useState<ObraEnriquecida | null>(null);
  const [descartarObra, setDescartarObra] = useState<ObraEnriquecida | null>(null);
  const [acaoId, setAcaoId] = useState<{ id: number; tipo: string } | null>(null);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'obras_triagem', password },
      });
      if (invokeError) throw new Error(await extractErrorMessage(invokeError));
      const parsed = data as { obras?: ObraCompleta[]; decisoes?: Record<string, TriagemDecisao>; oportunidades?: Record<string, OportunidadeVinculo>; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setObras(parsed.obras ?? []);
      setDecisoes(parsed.decisoes ?? {});
      setOportunidades(parsed.oportunidades ?? {});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar obras.');
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const enriquecidas: ObraEnriquecida[] = useMemo(() => {
    return obras.map((o) => ({
      ...o,
      triagem: calcularTriagem(o),
      decisao: decisoes[String(o.id)] ?? null,
      oportunidade: oportunidades[String(o.id)] ?? null,
    }));
  }, [obras, decisoes, oportunidades]);

  const stats = useMemo(() => {
    const total = enriquecidas.length;
    const novas = enriquecidas.filter((o) => !o.decisao && !o.oportunidade).length;
    const alta = enriquecidas.filter((o) => o.triagem.total >= 85).length;
    const monitorando = enriquecidas.filter((o) => o.decisao?.decisao === 'monitorar').length;
    const convertidas = enriquecidas.filter((o) => o.oportunidade).length;
    const descartadas = enriquecidas.filter((o) => o.decisao?.decisao === 'descartar').length;
    return { total, novas, alta, monitorando, convertidas, descartadas };
  }, [enriquecidas]);

  const cidades = useMemo(() => Array.from(new Set(obras.map((o) => o.cidade).filter(Boolean))).sort(), [obras]);
  const ufs = useMemo(() => Array.from(new Set(obras.map((o) => o.uf).filter(Boolean))).sort(), [obras]);
  const fontes = useMemo(() => Array.from(new Set(obras.map((o) => o.fonte).filter(Boolean))).sort(), [obras]);

  const filtradas = useMemo(() => {
    const hoje = new Date();
    return enriquecidas.filter((o) => {
      if (faixa && o.triagem.faixa !== faixa) return false;
      const seg = String(o.segmento ?? '').toLowerCase();
      if (segmento === 'institucional' && !seg.includes('institucional')) return false;
      if (segmento === 'corporativo' && !seg.includes('corporativo')) return false;
      if (segmento === 'residencial' && !seg.includes('residencial')) return false;
      if (natureza && o.triagem.natureza !== natureza) return false;
      if (situacao === 'nao_analisada' && (o.decisao || o.oportunidade)) return false;
      if (situacao === 'monitorando' && o.decisao?.decisao !== 'monitorar') return false;
      if (situacao === 'descartada' && o.decisao?.decisao !== 'descartar') return false;
      if (situacao === 'convertida' && !o.oportunidade) return false;
      if (cidade && String(o.cidade ?? '').toLowerCase() !== cidade.toLowerCase()) return false;
      if (uf && String(o.uf ?? '').toUpperCase() !== uf.toUpperCase()) return false;
      if (fonte && o.fonte !== fonte) return false;
      if (periodo && o.data_inicio) {
        const ini = new Date(o.data_inicio);
        const meses = (hoje.getTime() - ini.getTime()) / (30 * 86400000);
        if (periodo === '6m' && meses > 6) return false;
        if (periodo === '12m' && (meses <= 6 || meses > 12)) return false;
        if (periodo === '24m' && (meses <= 12 || meses > 24)) return false;
        if (periodo === '24m+' && meses <= 24) return false;
      }
      if (search) {
        const alvo = [o.contratante, o.profissional, o.cidade, o.bairro, o.rrt_numero, o.segmento]
          .filter(Boolean).join(' ').toLowerCase();
        if (!alvo.includes(search.toLowerCase())) return false;
      }
      return true;
    }).sort((a, b) => b.triagem.total - a.triagem.total || a.id - b.id);
  }, [enriquecidas, faixa, segmento, natureza, situacao, cidade, uf, fonte, periodo, search]);

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const criarOportunidade = async (obra: ObraEnriquecida) => {
    setAcaoId({ id: obra.id, tipo: 'criar' });
    setFeedback(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'obra_para_oportunidade', password, obra_id: obra.id },
      });
      if (invokeError) throw new Error(await extractErrorMessage(invokeError));
      const parsed = data as { existente?: boolean; oportunidade?: { id: number; titulo: string; status: string }; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      const op = parsed.oportunidade;
      if (op) {
        setOportunidades((prev) => ({ ...prev, [String(obra.id)]: { id: op.id, titulo: op.titulo, status: op.status } }));
        setFeedback({ ok: true, msg: parsed.existente ? 'Esta obra já possuía uma oportunidade.' : 'Oportunidade criada no Radar.' });
      }
    } catch (e) {
      setFeedback({ ok: false, msg: e instanceof Error ? e.message : 'Erro ao criar oportunidade.' });
    } finally {
      setAcaoId(null);
    }
  };

  const salvarDecisao = async (obra: ObraEnriquecida, decisao: 'monitorar' | 'descartar', motivo?: string, observacao?: string) => {
    setAcaoId({ id: obra.id, tipo: decisao });
    setFeedback(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'obra_triagem_salvar', password, obra_id: obra.id, decisao, motivo, observacao },
      });
      if (invokeError) throw new Error(await extractErrorMessage(invokeError));
      const parsed = data as { decisao?: TriagemDecisao | null; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      if (parsed.decisao) {
        setDecisoes((prev) => ({ ...prev, [String(obra.id)]: parsed.decisao as TriagemDecisao }));
      } else {
        setDecisoes((prev) => {
          const next = { ...prev };
          delete next[String(obra.id)];
          return next;
        });
      }
      setFeedback({ ok: true, msg: decisao === 'monitorar' ? 'Obra marcada para monitoramento.' : 'Obra descartada da triagem.' });
    } catch (e) {
      setFeedback({ ok: false, msg: e instanceof Error ? e.message : 'Erro ao salvar decisão.' });
    } finally {
      setAcaoId(null);
    }
  };

  const reabrir = async (obra: ObraEnriquecida) => {
    setAcaoId({ id: obra.id, tipo: 'reabrir' });
    setFeedback(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'obra_triagem_salvar', password, obra_id: obra.id, decisao: null },
      });
      if (invokeError) throw new Error(await extractErrorMessage(invokeError));
      const parsed = data as { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setDecisoes((prev) => {
        const next = { ...prev };
        delete next[String(obra.id)];
        return next;
      });
      setFeedback({ ok: true, msg: 'Obra reaberta para análise.' });
    } catch (e) {
      setFeedback({ ok: false, msg: e instanceof Error ? e.message : 'Erro ao reabrir.' });
    } finally {
      setAcaoId(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
          <span>{error}</span>
          <button onClick={loadData} className="text-xs font-semibold underline cursor-pointer whitespace-nowrap">Tentar novamente</button>
        </div>
      )}

      {/* Visão rápida */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Total de obras" value={stats.total} tone="text-gray-900" icon="ri-building-4-line" onClick={() => { setSituacao(''); setFaixa(''); }} />
        <StatCard label="Novas p/ analisar" value={stats.novas} tone="text-[#E8610A]" icon="ri-inbox-line" onClick={() => setSituacao('nao_analisada')} />
        <StatCard label="Alta prioridade" value={stats.alta} tone="text-red-600" icon="ri-fire-fill" onClick={() => setFaixa('analisar_agora')} />
        <StatCard label="Em monitoramento" value={stats.monitorando} tone="text-blue-600" icon="ri-eye-line" onClick={() => setSituacao('monitorando')} />
        <StatCard label="Convertidas" value={stats.convertidas} tone="text-green-600" icon="ri-radar-line" onClick={() => setSituacao('convertida')} />
        <StatCard label="Descartadas" value={stats.descartadas} tone="text-gray-500" icon="ri-close-circle-line" onClick={() => setSituacao('descartada')} />
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Select label="Prioridade" value={faixa} onChange={setFaixa}>
            <option value="">Todas</option>
            {TRIAGEM_FAIXAS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </Select>
          <Select label="Segmento" value={segmento} onChange={setSegmento}>
            <option value="">Todos</option>
            <option value="institucional">Institucional</option>
            <option value="corporativo">Corporativo</option>
            <option value="residencial">Residencial</option>
          </Select>
          <Select label="Natureza" value={natureza} onChange={setNatureza}>
            <option value="">Todas</option>
            <option value="obra_publica">{NATUREZA_OBRA_LABELS.obra_publica}</option>
            <option value="obra_nao_identificada">{NATUREZA_OBRA_LABELS.obra_nao_identificada}</option>
          </Select>
          <Select label="Situação" value={situacao} onChange={setSituacao}>
            <option value="">Todas</option>
            <option value="nao_analisada">Não analisada</option>
            <option value="monitorando">Em monitoramento</option>
            <option value="descartada">Descartada</option>
            <option value="convertida">Convertida em oportunidade</option>
          </Select>
          <Select label="Cidade" value={cidade} onChange={setCidade}>
            <option value="">Todas</option>
            {cidades.map((c) => <option key={c} value={c ?? ''}>{c}</option>)}
          </Select>
          <Select label="UF" value={uf} onChange={setUf}>
            <option value="">Todas</option>
            {ufs.map((u) => <option key={u} value={u ?? ''}>{u}</option>)}
          </Select>
          <Select label="Fonte" value={fonte} onChange={setFonte}>
            <option value="">Todas</option>
            {fontes.map((f) => <option key={f} value={f ?? ''}>{f}</option>)}
          </Select>
          <Select label="Período (início)" value={periodo} onChange={setPeriodo}>
            <option value="">Todos</option>
            <option value="6m">Últimos 6 meses</option>
            <option value="12m">6–12 meses</option>
            <option value="24m">12–24 meses</option>
            <option value="24m+">Mais de 24 meses</option>
          </Select>
        </div>
        <div className="mt-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar contratante, responsável, RRT, bairro..."
            className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 bg-white"
          />
        </div>
      </div>

      {feedback && (
        <div className={`text-sm border rounded-lg px-4 py-3 ${feedback.ok ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-600 bg-red-50 border-red-200'}`}>
          {feedback.msg}
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtradas.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-16">Nenhuma obra encontrada com os filtros atuais.</div>
      ) : (
        <div className="space-y-3">
          {filtradas.map((o) => {
            const f = faixaInfo(o.triagem.faixa);
            const isExpanded = expanded.has(o.id);
            const temOportunidade = !!o.oportunidade;
            const decisao = o.decisao;
            return (
              <div key={o.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-start gap-4">
                  {/* Score */}
                  <div className={`shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center border ${f.tone}`}>
                    <span className="text-lg font-bold leading-none text-gray-900">{o.triagem.total}</span>
                    <span className="text-[9px] text-gray-500 mt-0.5">/100</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 truncate max-w-xs">{o.contratante || `RRT ${o.rrt_numero ?? ''}`}</span>
                      <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 border ${f.tone}`}>{f.label}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500 mt-1">
                      <span className="whitespace-nowrap"><i className="ri-map-pin-line mr-0.5"></i>{[o.cidade, o.uf].filter(Boolean).join(' - ')}</span>
                      <span className={`px-2 py-0.5 rounded-full font-semibold ${segmentoTone(o.segmento)}`}>{o.segmento ?? '—'}</span>
                      <span className="whitespace-nowrap"><i className="ri-ruler-line mr-0.5"></i>{formatArea(o.area_max)}</span>
                      <span className="whitespace-nowrap"><i className="ri-calendar-line mr-0.5"></i>{formatDate(o.data_inicio)}</span>
                      <span className="whitespace-nowrap text-gray-400">{NATUREZA_OBRA_LABELS[o.triagem.natureza]}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      {temOportunidade && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">
                          <i className="ri-check-line"></i> OPORTUNIDADE CRIADA
                        </span>
                      )}
                      {decisao?.decisao === 'monitorar' && (
                        <span className="text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">Monitorando</span>
                      )}
                      {decisao?.decisao === 'descartar' && (
                        <span className="text-[11px] font-bold bg-gray-100 text-gray-500 border border-gray-200 rounded-full px-2 py-0.5">
                          Descartada{decisao.motivo ? ` · ${MOTIVO_DESCARTE_OBRA_LABELS[decisao.motivo] ?? decisao.motivo}` : ''}
                        </span>
                      )}
                      {o.empresa_id && (
                        <span className="text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-2 py-0.5">Empresa relacionada</span>
                      )}
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="shrink-0 flex flex-col items-end gap-1.5">
                    <button onClick={() => setDetalhe(o)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors">
                      <i className="ri-search-eye-line"></i> ANALISAR
                    </button>
                    <button
                      onClick={() => toggleExpand(o.id)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#E8610A] hover:underline cursor-pointer whitespace-nowrap"
                    >
                      <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i> Por quê?
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    <p className="text-xs font-semibold text-gray-700">Prioridade de triagem: {o.triagem.total}</p>
                    {o.triagem.fatores.map((fator) => (
                      <div key={fator.key} className="flex items-start gap-3 text-xs">
                        <span className="w-24 shrink-0 text-gray-500">+ {fator.pontos} · {fator.label}</span>
                        <span className="text-gray-600 flex-1">{fator.motivo}</span>
                      </div>
                    ))}
                    {o.triagem.dataInconsistente && (
                      <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <i className="ri-alert-line mt-0.5"></i>
                        <span><strong>Data inconsistente.</strong> {o.triagem.dataInconsistenteMotivo}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      {!temOportunidade && (
                        <button
                          onClick={() => criarOportunidade(o)}
                          disabled={acaoId?.id === o.id}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
                        >
                          <i className={`${acaoId?.id === o.id && acaoId.tipo === 'criar' ? 'ri-loader-4-line animate-spin' : 'ri-radar-line'}`}></i>
                          Criar oportunidade no Radar
                        </button>
                      )}
                      {!temOportunidade && !decisao && (
                        <button
                          onClick={() => salvarDecisao(o, 'monitorar')}
                          disabled={acaoId?.id === o.id}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#E8610A] bg-[#E8610A]/10 hover:bg-[#E8610A]/20 px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
                        >
                          <i className="ri-eye-line"></i> Monitorar
                        </button>
                      )}
                      {!temOportunidade && decisao?.decisao !== 'descartar' && (
                        <button
                          onClick={() => setDescartarObra(o)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                        >
                          <i className="ri-close-circle-line"></i> Descartar
                        </button>
                      )}
                      {temOportunidade && (
                        <button
                          onClick={() => setDetalhe(o)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                        >
                          <i className="ri-external-link-line"></i> Abrir oportunidade
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {detalhe && (
        <TriagemDetailModal
          obra={detalhe}
          onClose={() => setDetalhe(null)}
          onCriarOportunidade={() => criarOportunidade(detalhe)}
          criando={acaoId?.id === detalhe.id && acaoId.tipo === 'criar'}
          onMonitorar={() => salvarDecisao(detalhe, 'monitorar')}
          monitorando={acaoId?.id === detalhe.id && acaoId.tipo === 'monitorar'}
          onDescartar={() => { setDescartarObra(detalhe); }}
          onReabrir={() => reabrir(detalhe)}
        />
      )}

      {descartarObra && (
        <TriagemDescarteModal
          obra={descartarObra}
          onClose={() => setDescartarObra(null)}
          saving={acaoId?.id === descartarObra.id && acaoId.tipo === 'descartar'}
          onConfirm={(motivo, observacao) => {
            salvarDecisao(descartarObra, 'descartar', motivo, observacao);
            setDescartarObra(null);
          }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, tone, icon, onClick }: { label: string; value: number; tone: string; icon: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-4 text-left w-full cursor-pointer hover:border-[#E8610A]/40 transition-colors"
    >
      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center justify-between">
        {label}
        <i className={`${icon} ${tone}`}></i>
      </p>
      <p className={`text-2xl font-bold ${tone}`}>{value}</p>
    </button>
  );
}

function Select({ label, value, onChange, children }: { label: string; value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] bg-white"
      >
        {children}
      </select>
    </div>
  );
}